"use client";

import React from "react";
import { Carousel, CarouselContent, CarouselItem, type CarouselApi } from "@/components/carousel/carousel";
import { useCarouselAutoplay } from "@/components/carousel/useCarouselAutoplay";
import CardContentItem from "@/components/cards/cardContent/cardContent";
import LazyMount from "@/components/utilities/LazyMount";
import { Separator } from "@/components/separator/separator";
import { PaginationArrows } from "@/components/paginationArrows/paginationArrows";
import { buildLoopCards } from "./testimonialLoop";
import type { ContentCard } from "@/types/cms";
import styles from "./CardSection.module.css";

type CardVariant = "default" | "testimonial" | "price" | "lesson" | "agentAvatar";

/**
 * Testimonial carousel (autoplay, centered, all breakpoints). Extracted from
 * CardSection so it can be dynamically imported — embla-carousel +
 * useCarouselAutoplay load only when a testimonial section renders.
 */
export default function TestimonialCarousel({
  content_cards,
  getCardVariant,
  sectionId,
  sectionClass,
  containerClass,
  callout,
  showDivider,
  dividerClass,
  Header,
}: {
  content_cards: ContentCard[];
  getCardVariant: (card: ContentCard) => CardVariant;
  sectionId: string;
  sectionClass: string;
  containerClass: string;
  callout: boolean;
  showDivider: boolean;
  dividerClass: string;
  Header: () => React.JSX.Element;
}) {
  const [api, setApi] = React.useState<CarouselApi>();

  // Enable autoplay with 3 second delay; expose play/pause for the controls.
  const { isPlaying, toggle, interact } = useCarouselAutoplay(api, 3000);

  // The testimonial slides are a fixed width, so a handful of cards can't fill a
  // wide viewport — embla would silently drop `loop` (canLoop() === false) and
  // leave trailing blank space. Repeat the cards enough to always overflow the
  // widest viewport so the loop never disables. See ./testimonialLoop.
  const loopCards = buildLoopCards(content_cards);

  return (
    <section
      id={sectionId}
      className={callout ? `${sectionClass} ${styles.callout}` : sectionClass}
    >
      <div className={containerClass}>
        {showDivider && (
          <div className={dividerClass}><Separator /></div>
        )}
        <Header />
        <div className={styles.testimonialCarouselWrap}>
          {/* Defer embla init (slide-measurement reflow) until the carousel
              nears the viewport. Autoplay no-ops until `api` exists, i.e. until
              this mounts on approach. minHeight reserves space for the swap —
              matched to the card's own `min-height: 216px` (TestimonialCard) so
              it doesn't leave dead space below the cards before the controls. */}
          <LazyMount style={{ minHeight: 216 }}>
            <Carousel
              setApi={setApi}
              opts={{
                align: "center",
                dragFree: false,
                slidesToScroll: 1,
                loop: true,
              }}
            >
              <CarouselContent className={styles.testimonialCarouselContent}>
                {loopCards.map((card, idx) => (
                  <CarouselItem
                    key={`${idx}-${card.id ?? card.header ?? 'card'}`}
                    className={`reveal-item reveal-fade ${styles.testimonialCarouselItem}`}
                    // All cards share index 1 (header is 0) so they fade in
                    // as one unit. A per-card stagger read scrambled here:
                    // the centered loop puts the LAST card visually first,
                    // so DOM-order delays didn't match what's on screen.
                    style={{ "--reveal-index": 1 } as React.CSSProperties}
                  >
                    <CardContentItem card={card} variant={getCardVariant(card)} />
                  </CarouselItem>
                ))}
              </CarouselContent>
            </Carousel>
          </LazyMount>
        </div>
        {/* Always rendered (not gated on `api`) so the row reserves its space —
            gating caused a layout shift when it popped in on carousel mount.
            Handlers no-op until the LazyMount-deferred carousel sets `api`. */}
        <PaginationArrows
          isPlaying={isPlaying}
          onPlayPause={toggle}
          onPrev={() => {
            api?.scrollPrev();
            interact();
          }}
          onNext={() => {
            api?.scrollNext();
            interact();
          }}
          prevLabel="Previous testimonial"
          nextLabel="Next testimonial"
        />
      </div>
    </section>
  );
}
